import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader, CheckCircle, XCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { SUPABASE_CONFIG } from '../config/supabase';

const PaymentCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'verifying' | 'success' | 'failed'>('verifying');
  const [message, setMessage] = useState('Verifying your payment...');
  const [credits, setCredits] = useState(0);

  useEffect(() => {
    const reference = searchParams.get('reference'); // Paystack format
    const trxref = searchParams.get('trxref'); // Alternative Paystack param
    const paddleTxn = searchParams.get('_ptxn'); // Paddle format
    
    const paymentReference = reference || trxref || paddleTxn;

    if (!paymentReference) {
      setStatus('failed');
      setMessage('No payment reference found');
      return;
    }

    verifyPayment(paymentReference);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const verifyPayment = async (reference: string) => {
    try {
      const token = SUPABASE_CONFIG.anonKey;

      const response = await fetch(
        `${SUPABASE_CONFIG.url}/functions/v1/verify-payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ reference }),
        }
      );

      const result = await response.json();

      if (result.status === 'success') {
        setStatus('success');
        setMessage('Payment successful!');
        setCredits(result.credits || 0);
        
        // Refresh credits in the global state
        if ((window as any).refreshCredits) {
          (window as any).refreshCredits();
        }
        
        // Redirect to home after 3 seconds
        setTimeout(() => {
          navigate('/home');
        }, 3000);
      } else {
        setStatus('failed');
        setMessage(result.message || 'Payment verification failed');
        
        // Redirect back to buy credits after 3 seconds
        setTimeout(() => {
          navigate('/buy-credits');
        }, 3000);
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setStatus('failed');
      setMessage('Failed to verify payment. Please contact support.');
      
      setTimeout(() => {
        navigate('/buy-credits');
      }, 3000);
    }
  };

  return (
    <div className="app">
      <div className="container" style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '70vh',
        padding: '20px'
      }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            textAlign: 'center',
            maxWidth: '400px',
            width: '100%'
          }}
        >
          {status === 'verifying' && (
            <>
              <Loader 
                size={64} 
                className="animate-spin" 
                style={{ 
                  margin: '0 auto 20px',
                  color: 'var(--primary-color)'
                }} 
              />
              <h2 style={{ 
                fontSize: '24px', 
                fontWeight: '600',
                marginBottom: '12px',
                color: 'var(--text-primary)'
              }}>
                Verifying Payment
              </h2>
              <p style={{ 
                fontSize: '16px',
                color: 'var(--text-secondary)'
              }}>
                Please wait while we confirm your payment...
              </p>
            </>
          )}

          {status === 'success' && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            >
              <CheckCircle 
                size={80} 
                color="#10B981" 
                style={{ margin: '0 auto 20px' }}
              />
              <h2 style={{ 
                fontSize: '28px', 
                fontWeight: '700',
                marginBottom: '12px',
                color: 'var(--text-primary)'
              }}>
                Payment Successful!
              </h2>
              <p style={{ 
                fontSize: '18px',
                color: 'var(--text-secondary)',
                marginBottom: '8px'
              }}>
                {credits} tours added to your account
              </p>
              <p style={{ 
                fontSize: '14px',
                color: 'var(--text-light)'
              }}>
                Redirecting to home...
              </p>
              
              <button
                className="btn btn-primary"
                onClick={() => navigate('/home')}
                style={{ marginTop: '24px' }}
              >
                Continue
              </button>
            </motion.div>
          )}

          {status === 'failed' && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            >
              <XCircle 
                size={80} 
                color="#EF4444" 
                style={{ margin: '0 auto 20px' }}
              />
              <h2 style={{ 
                fontSize: '28px', 
                fontWeight: '700',
                marginBottom: '12px',
                color: 'var(--text-primary)'
              }}>
                Payment Failed
              </h2>
              <p style={{ 
                fontSize: '16px',
                color: 'var(--text-secondary)',
                marginBottom: '8px'
              }}>
                {message}
              </p>
              <p style={{ 
                fontSize: '14px',
                color: 'var(--text-light)'
              }}>
                Redirecting back...
              </p>
              
              <button
                className="btn btn-primary"
                onClick={() => navigate('/buy-credits')}
                style={{ marginTop: '24px' }}
              >
                Try Again
              </button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentCallback;

